import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command';
import type { AppMenuConfig, AppMenuItem } from '@workspace/ui';

type QuickAction = {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  onSelect: () => void;
};

type CommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menu: AppMenuConfig;
  recents: AppMenuItem[];
  onNavigate: (path: string) => void;
  onRecordRecent?: (id: string) => void;
  quickActions?: QuickAction[];
  initialQuery?: string;
  onQueryChange?: (query: string) => void;
};

type CommandResult = {
  id: string;
  label: string;
  type: 'nav' | 'action';
  onSelect: () => void;
};

function flattenMenu(menu: AppMenuConfig): AppMenuItem[] {
  const results: AppMenuItem[] = [];
  const walk = (items?: AppMenuItem[]) => {
    items?.forEach(item => {
      results.push(item);
      if (item.children) {
        walk(item.children);
      }
    });
  };
  walk(menu.sideMenu);
  walk(menu.topMenu);
  walk(menu.userMenu);
  return results;
}

export function CommandPalette({
  open,
  onOpenChange,
  menu,
  recents,
  onNavigate,
  onRecordRecent,
  quickActions = [],
  initialQuery = '',
  onQueryChange
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wasOpenRef = useRef(false);

  const setQueryAndSync = useCallback(
    (next: string) => {
      setQuery(next);
      onQueryChange?.(next);
    },
    [onQueryChange]
  );

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || target?.getAttribute('contenteditable') === 'true';
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onOpenChange(true);
        return;
      }
      if (!isTyping && !event.metaKey && !event.ctrlKey && event.key === '/') {
        event.preventDefault();
        onOpenChange(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpenChange]);

  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    if (open && !wasOpen) {
      setQueryAndSync(initialQuery);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
    if (!open && wasOpen) {
      setQueryAndSync('');
    }
    wasOpenRef.current = open;
  }, [initialQuery, open, setQueryAndSync]);

  const actionResults = useMemo<CommandResult[]>(() => {
    const q = query.trim().toLowerCase();
    return quickActions
      .filter(action => !q || action.label.toLowerCase().includes(q) || action.description?.toLowerCase().includes(q))
      .map(action => ({
        id: `action:${action.id}`,
        label: action.label,
        type: 'action' as const,
        onSelect: action.onSelect
      }));
  }, [quickActions, query]);

  const recentResults = useMemo<CommandResult[]>(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      return [];
    }
    return recents.map(item => ({
      id: `recent:${item.id}`,
      label: item.label,
      type: 'nav' as const,
      onSelect: () => {
        if (item.route) {
          onNavigate(item.route);
          onRecordRecent?.(item.id);
        }
      }
    }));
  }, [query, recents, onNavigate, onRecordRecent]);

  const navResults = useMemo<CommandResult[]>(() => {
    const q = query.trim().toLowerCase();
    return flattenMenu(menu)
      .filter(item => item.route)
      .filter(item => !q || item.label?.toLowerCase().includes(q))
      .map(item => ({
        id: `nav:${item.id}`,
        label: item.label,
        type: 'nav' as const,
        onSelect: () => {
          if (item.route) {
            onNavigate(item.route);
            onRecordRecent?.(item.id);
          }
        }
      }));
  }, [menu, onNavigate, onRecordRecent, query]);

  const hasQuery = query.trim().length > 0;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        ref={inputRef}
        value={query}
        onValueChange={setQueryAndSync}
        placeholder="Type a command or search..."
        aria-label="Type a command or search..."
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {!hasQuery && actionResults.length > 0 ? (
          <CommandGroup heading="Actions">
            {actionResults.map(result => (
              <CommandItem
                key={result.id}
                value={result.id}
                onSelect={() => {
                  result.onSelect();
                  onOpenChange(false);
                }}
              >
                <span>{result.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {!hasQuery && recentResults.length > 0 ? (
          <>
            {actionResults.length > 0 ? <CommandSeparator /> : null}
            <CommandGroup heading="Recent">
              {recentResults.map(result => (
                <CommandItem
                  key={result.id}
                  value={result.id}
                onSelect={() => {
                  result.onSelect();
                  onOpenChange(false);
                }}
              >
                <span>{result.label}</span>
              </CommandItem>
            ))}
            </CommandGroup>
          </>
        ) : null}

        {navResults.length > 0 ? (
          <>
            {!hasQuery && (actionResults.length > 0 || recentResults.length > 0) ? <CommandSeparator /> : null}
            <CommandGroup heading={hasQuery ? 'Results' : 'Pages'}>
              {navResults.map(result => (
                <CommandItem
                  key={result.id}
                  value={result.id}
                onSelect={() => {
                  result.onSelect();
                  onOpenChange(false);
                }}
              >
                <span>{result.label}</span>
              </CommandItem>
            ))}
            </CommandGroup>
          </>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}

export default CommandPalette;
